import React, { useState } from "react"

import { detect } from "detect-browser"
import { Field, Form, Formik } from "formik"
import * as Yup from "yup"

import { AlertLevel } from "~tailwindui/types/enums"

import { AlertMessageBox } from "~tailwindui/Basics"

import { gql, useClient, useMutation } from "hooks/urql"

import { ErrorMessage, SubmitButton } from "components/Forms/Formik"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"

import { FormFailures, handleFailure } from "../Forms/Formik/hookComponents"

const ValidationSchema = Yup.object().shape({
  phoneNumber: Yup.string().required("Please specify a contact number"),
})

export type StudentModalProps = {
  phoneNumber: string
  sessionId: string
  error: { name: string; message: string }
  className?: string
}

const StudentModal: React.FC<StudentModalProps> = ({
  phoneNumber,
  sessionId,
  error,
  className,
}) => {
  const browser = detect()
  const [successMessage, setSuccessMessage] = useState<string | null>()

  const client = useClient()
  const [, runMutation]: any[] = useMutation(supportMutation, client)

  const formState = {
    sessionId: sessionId,
    errorName: error?.name,
    errorMessage: error?.message,
    phoneNumber: phoneNumber,
    browserName: browser?.name,
    browserVersion: browser?.version,
    os: browser?.os,
  }

  const handleSubmit = (values, actions) => {
    runMutation({ input: values })
      .then(
        response => {
          const { failures } = response.data.supportRequest
          if (failures.length > 0) {
            handleFailure(actions, failures)
          } else {
            setSuccessMessage(
              "A member of our support team will text you at the number you provided"
            )
          }
        },
        () => handleFailure(actions)
      )
      .catch(() => handleFailure(actions))
      .finally(() => actions.setSubmitting(false))
  }

  return (
    <ModalWithProvidedBody
      buttonText="Contact Support"
      buttonClassName={className || "btn solid green mr-1"}
      modalTitle="Support Request"
      modalClassName="bootstrap-modal"
    >
      <Formik
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        initialValues={formState}
      >
        {({ setFieldValue, isSubmitting }) => (
          <Form>
            <div className="modal-body">
              <div className="form-group">
                <label className="w-100">
                  Contact Number (must be able to receive texts)
                  <Field
                    name="phoneNumber"
                    type="tel"
                    onChange={e =>
                      setFieldValue(
                        "phoneNumber",
                        e.currentTarget.value.replace(/\D+/g, "")
                      )
                    }
                  />
                </label>
                <ErrorMessage name="phoneNumber" />
              </div>
            </div>
            <div className="modal-footer flex-column flex">
              <FormFailures />
              {successMessage && (
                <AlertMessageBox level={AlertLevel.Success}>
                  {successMessage}
                </AlertMessageBox>
              )}
              {!isSubmitting && !successMessage && (
                <SubmitButton isSubmitting={isSubmitting} text="Submit" />
              )}
            </div>
          </Form>
        )}
      </Formik>
    </ModalWithProvidedBody>
  )
}

const supportMutation = gql`
  mutation ($input: SupportRequestInputObject!) {
    supportRequest(input: $input) {
      failures {
        message
      }
    }
  }
`

export default StudentModal
